import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
} from "@xyflow/react";

import { MARKER_IDS } from "./edge-marker-ids";

const NESTING_LEVEL_COLORS = [
  "#EC4899", // Pink (level 0)
  "#8B5CF6", // Purple (level 1)
  "#3B82F6", // Blue (level 2)
  "#10B981", // Green (level 3)
  "#F59E0B", // Amber (level 4)
];

const NESTING_LEVEL_MARKERS = [
  MARKER_IDS.PINK, // Level 0
  MARKER_IDS.PURPLE, // Level 1
  MARKER_IDS.BLUE, // Level 2
  MARKER_IDS.GREEN, // Level 3
  MARKER_IDS.AMBER, // Level 4
];

function getEdgeStyling(
  isMapChild: boolean,
  selected: boolean,
  nestingLevel?: number,
): { stroke: string; strokeWidth: string; markerEnd: string } {
  if (isMapChild) {
    const level = nestingLevel ?? 0;
    const colorIndex = level % NESTING_LEVEL_COLORS.length;
    return {
      stroke: NESTING_LEVEL_COLORS[colorIndex],
      strokeWidth: selected ? "2.5px" : "1px",
      markerEnd: NESTING_LEVEL_MARKERS[colorIndex],
    };
  }

  return {
    stroke: "#71717A",
    strokeWidth: selected ? "2.5px" : "1px",
    markerEnd: MARKER_IDS.GREY,
  };
}

export const Edge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  selected,
  data,
  sourcePosition,
  targetPosition,
}: EdgeProps) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const { stroke, strokeWidth, markerEnd } = getEdgeStyling(
    (data?.isMapChild ?? false) as boolean,
    selected ?? false,
    data?.nestingLevel as number | undefined,
  );

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke,
          strokeWidth,
        }}
        data-testid="edge-path"
        markerEnd={`url(#${markerEnd})`}
      />
      {data?.label && (
        <EdgeLabelRenderer>
          <EdgeLabel
            transform={`translate(-50%, 0%) translate(${labelX}px,${labelY}px)`}
            label={data.label as string}
          />
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const EdgeLabel = ({
  transform,
  label,
}: {
  transform: string;
  label: string;
}) => {
  return (
    <div
      style={{
        transform,
      }}
      data-testid="edge-label"
      className="nodrag nopan bg-background absolute z-[10001] rounded-md border border-gray-800 px-2 py-1 text-xs dark:border-gray-400"
    >
      {label}
    </div>
  );
};
